<script setup lang="ts">
import {
  EvaluationCriteria,
  EvaluationRelevantInstance,
  EvaluationResults,
} from "types/EvaluationResults";

const props = defineProps({
  documentId: {
    type: String,
    required: true,
  },
  evaluationTest: {
    type: Object as PropType<EvaluationTest>,
    required: true,
  },
});

const emit = defineEmits(["save"]);

const documentSelectionStore = useDocumentSelectionStore(props.documentId);
const { hasSelection, selectedTokens } = storeToRefs(documentSelectionStore);

const convertTokensToText = computed(() => {
  return (tokens: ParagraphToken[]) => {
    return tokens
      .map((token) => token.text)
      .join("")
      .trim();
  };
});

const convert = async () => {
  if (props.evaluationTest == undefined) return;

  if (hasSelection.value != true) return;
  const paragraphs = useDocumentSelectionStore(
    props.documentId
  ).selectedParagraphs;

  if (paragraphs.length == 0) return;

  const paragraph = paragraphs[0];

  const paragraphCopy = { ...paragraph };

  paragraphCopy.tokens = selectedTokens.value.map((token) => {
    if (token.indicators && token.indicators.types?.includes("dressUp")) {
      token.indicators.subTypes = [];
    }

    return {
      ...token,
    };
  });

  paragraphCopy.text = convertTokensToText.value(selectedTokens.value);

  const newParagraphs = [paragraphCopy];

  props.evaluationTest.documentText = convertTokensToText.value(
    selectedTokens.value
  );
  props.evaluationTest.documentToTest = {
    path: `/testDocuments/${props.evaluationTest.id}/revisions/${props.evaluationTest.originalReportedProblem.revisionId}`,
  };

  emit("save", props.evaluationTest);
  await useReportedProblems().copyDocument(
    props.evaluationTest.id!,
    props.evaluationTest.originalReportedProblem.documentId,
    props.evaluationTest.originalReportedProblem.revisionId,
    newParagraphs
  );

  documentSelectionStore.clearSelection();
};

const isGeneratingTestData = ref(false);

const paragraphSentencesMetricId = ref("bXgWKvXRUHuDWdOeuvBC");

const extractResultsFromCriteria = (
  paragraphCriteria: EvaluationCriteria,
  relevantInstance: EvaluationRelevantInstance
) => {
  const paragraphInstance = paragraphCriteria.relevantInstances[0];
  const sentences = paragraphInstance.criteria[0].relevantInstances;

  const firstToken = relevantInstance.tokens[0];

  const sentence = sentences.find((sentence) => {
    return (
      sentence.tokens.find((token) => token.id == firstToken.id) != undefined
    );
  });

  return sentence;
};

const onGenerateTestData = async () => {
  try {
    if (!props.evaluationTest) return;

    isGeneratingTestData.value = true;

    if (props.evaluationTest.originalReportedProblem.problem == undefined)
      return;

    let relevantInstance = undefined as EvaluationRelevantInstance | undefined;
    if (props.evaluationTest.originalReportedProblem.problem.relevantInstance) {
      relevantInstance =
        props.evaluationTest.originalReportedProblem.problem.relevantInstance;
    } else {
      relevantInstance = props.evaluationTest.originalReportedProblem
        .problem as EvaluationRelevantInstance;
    }

    if (!relevantInstance) {
      isGeneratingTestData.value = false;
      return;
    }

    const firstToken = relevantInstance.tokens[0];

    if (!firstToken) return;

    const tokenGlobalPosition = useDocumentTokenIdPositionMapStore(
      props.documentId
    ).tokenGlobalPositionFromId(firstToken.id);

    const tokenStore = useParagraphTokenStore(
      props.documentId,
      tokenGlobalPosition
    );

    const paragraphId = tokenStore.paragraphId;

    if (!paragraphId) return;

    const paragraphStore = useDocumentParagraphStore(
      props.documentId,
      paragraphId
    );

    const paragraph = paragraphStore.paragraph;

    if (!paragraph) return;

    const response = await $fetch(
      `/api/sys-admin/test/${paragraphSentencesMetricId.value}`,
      {
        method: "POST",
        headers: await useApiHeaders(),
        body: {
          paragraphs: [paragraph],
        },
      }
    );

    const evaluationResults = response as EvaluationResults;
    const evaluationResult = evaluationResults.evaluationResult;

    const sentence = extractResultsFromCriteria(
      evaluationResult[0],
      relevantInstance
    );

    if (!sentence) return;

    const tokens = sentence.tokens;
    tokens.sort((a, b) => a.position - b.position);
    const firstSentenceToken = tokens[0];
    const lastSentenceToken = tokens[tokens.length - 1];

    const firstTokenGlobalPosition = useDocumentTokenIdPositionMapStore(
      props.documentId
    ).tokenGlobalPositionFromId(firstSentenceToken.id);
    const lastTokenGlobalPosition = useDocumentTokenIdPositionMapStore(
      props.documentId
    ).tokenGlobalPositionFromId(lastSentenceToken.id);

    const allTokens = [] as ParagraphToken[];

    let tokenPosition = firstTokenGlobalPosition;

    while (tokenPosition <= lastTokenGlobalPosition) {
      const tokenStore = useParagraphTokenStore(
        props.documentId,
        tokenPosition
      );
      const token = tokenStore.token;
      if (token) {
        allTokens.push(token);
      }
      tokenPosition++;
    }

    const paragraphToTest = {
      ...paragraph,
      tokens: allTokens,
      text: convertTokensToText.value(allTokens),
    };

    const newParagraphs = [paragraphToTest] as any;

    props.evaluationTest.documentText = paragraphToTest.text;
    props.evaluationTest.documentToTest = {
      path: `/testDocuments/${props.evaluationTest.id}/revisions/${props.evaluationTest.originalReportedProblem.revisionId}`,
    };

    emit("save");
    await useReportedProblems().copyDocument(
      props.evaluationTest.id!,
      props.evaluationTest.originalReportedProblem.documentId,
      props.evaluationTest.originalReportedProblem.revisionId,
      newParagraphs
    );
  } catch (error) {}

  isGeneratingTestData.value = false;
};
</script>

<template>
  <div>
    <BaseTextButton
      :show-spinner="isGeneratingTestData"
      class=""
      @click="onGenerateTestData"
    >
      Generate Test Data
    </BaseTextButton>
    <TransitionsCollapsible>
      <div
        v-if="documentId && hasSelection"
        class="mt-2 p-2 border-2 border-dashed"
      >
        <div class="flex flex-row items-center">
          <h2 class="text-body-1 font-bold">Convert Selection to Document</h2>
          <span class="ml-2"> | </span>
          <BaseTextButton @click.prevent="convert"> Convert </BaseTextButton>
        </div>
        <span>
          {{ convertTokensToText(selectedTokens) }}
        </span>
      </div>
    </TransitionsCollapsible>
  </div>
</template>

<style scoped></style>
